/**
 * Vorlage der **Projektauslegung** (VB-26014, ADR 0081).
 *
 * Quelle ist das Haus-Excel `Projektauslegung_Rev06.xlsm`, Blatt „Datenblatt
 * Projektauslegung" (A1:E92): Beschriftung in Spalte A, Wert in Spalte E, dazu die
 * 33 Auswahllisten aus dem Blatt „Stammdaten". Beides ist hier zeilengenau
 * übernommen — Reihenfolge, Abschnitte und Optionen entsprechen dem Ausdruck, den
 * Konstruktion und Projektleitung heute von Hand ausfüllen.
 *
 * `mode` ist nur der STARTWERT einer Zeile. Jede Zeile lässt sich im Blatt
 * umstellen (Artikel ↔ Warengruppe ↔ Angabe) — die Vorlage entscheidet nicht
 * darüber, was eine Position ist, sie schlägt es vor.
 */

import type { ArticleUnit } from '../inventory/article.schema';

/** Was in einer Zeile steht — und ob daraus eine Stücklisten-Position wird. */
export const LAYOUT_ROW_MODES = ['ARTICLE', 'COMMODITY_GROUP', 'NOTE'] as const;
export type LayoutRowMode = (typeof LAYOUT_ROW_MODES)[number];

export interface LayoutTemplateRow {
  /** Stabiler Schlüssel der Vorlagenzeile (Wiedererkennung beim erneuten Laden). */
  key: string;
  section: string;
  label: string;
  /** Startmodus; im Blatt jederzeit umstellbar. */
  mode: LayoutRowMode;
  /** Mengen-Vorschlag für Positionszeilen (leer = der Nutzer trägt sie ein). */
  defaultQuantity?: number;
  unit?: ArticleUnit;
  /**
   * Warengruppe, in der die Artikelsuche startet bzw. die ein Platzhalter
   * vorschlägt. Reiner Vorschlag — gesucht werden kann im ganzen Stamm.
   */
  commodityGroupCode?: number;
  /** Auswahlliste aus dem Excel (Blatt „Stammdaten") für den Angabe-Modus. */
  options?: string[];
  /** Originaltext des Excel als Orientierung („üblich: …"). */
  hint?: string;
  /**
   * Feld am Projekt, das diese Zeile anzeigt und schreibt (einzige Quelle der
   * Wahrheit, §6) — dieselben Werte stehen im Register „Projektdaten".
   */
  projectField?: 'projectLanguage' | 'baseDesign' | 'certificates' | 'basinCount' | 'lengthM' | 'widthM' | 'heightM' | 'waterLevelM';
  /** Zahl statt Freitext (Anzahl, Maß). */
  isNumeric?: boolean;
}

/** Abschnitte in der Reihenfolge des Excel-Ausdrucks. */
export const LAYOUT_SECTIONS = [
  'Grundauslegung',
  'Beckendimensionierung',
  '1. Hauptwelle',
  '2. Antriebseinheit',
  '3. Balken',
  '4. Förderkette',
  '5. Umlenkstummel',
  '6. Rücklaufschiene',
  '7. Bodengleitstreifen',
  '8. Gleichlaufüberwachung',
  '9. Skimrinne',
  'Sonstiges',
] as const;
export type LayoutSection = (typeof LAYOUT_SECTIONS)[number];

/** Abschnitte, die Kopfdaten tragen (Felder-Raster statt Positionszeilen). */
export const LAYOUT_HEAD_SECTIONS: readonly string[] = ['Grundauslegung', 'Beckendimensionierung'];

const WERKSTOFFE = [
  '1.4301 SS304', '1.4307 SS304L', '1.4401 SS316', '1.4404 SS316L', '1.4571 SS316Ti',
  '1.4462 Duplex', '1.4410 Super Duplex', '1.4529', '1.4547', 'GFK',
];

export const LAYOUT_TEMPLATE_ROWS: LayoutTemplateRow[] = [
  // ── Grundauslegung ─────────────────────────────────────────────────────────
  { key: 'sprache', section: 'Grundauslegung', label: 'Sprache', mode: 'NOTE',
    projectField: 'projectLanguage', options: ['Deutsch', 'Englisch'] },
  { key: 'anzahl-raeumer', section: 'Grundauslegung', label: 'Anzahl Räumer', mode: 'NOTE',
    isNumeric: true, unit: 'PIECE', hint: 'steuert Mengen — im Excel z. B. 2 Sensoren je Räumer' },
  { key: 'grundauslegung', section: 'Grundauslegung', label: 'Grundauslegung', mode: 'NOTE',
    projectField: 'baseDesign',
    options: ['2 - Wellen - Räumer', '2 - Wellen - Räumer FLOTAT', '2- Wellen - Räumer BODEN',
      '3 - Wellen - Räumer', '3 - Wellen - Räumer FLOTAT', '3 - Wellen - Räumer BODEN', '4 - Wellen - Räumer'] },
  { key: 'raeumertyp', section: 'Grundauslegung', label: 'Räumertyp', mode: 'NOTE', options: ['Kommunal', 'API'] },
  { key: 'spezifikation', section: 'Grundauslegung', label: 'Spezifikation', mode: 'NOTE',
    options: ['Standard', 'EX Zone 0', 'EX Zone 1', 'EX Zone 2'] },
  { key: 'zertifikate', section: 'Grundauslegung', label: 'Zertifikate', mode: 'NOTE', projectField: 'certificates' },
  { key: 'itp', section: 'Grundauslegung', label: 'ITP', mode: 'NOTE' },
  { key: 'werkstoff', section: 'Grundauslegung', label: 'Material', mode: 'NOTE', options: WERKSTOFFE },

  // ── Beckendimensionierung (alles am Projekt, §6) ───────────────────────────
  { key: 'beckenanzahl', section: 'Beckendimensionierung', label: 'Beckenanzahl', mode: 'NOTE',
    isNumeric: true, unit: 'PIECE', projectField: 'basinCount' },
  { key: 'laenge', section: 'Beckendimensionierung', label: 'Länge', mode: 'NOTE',
    isNumeric: true, unit: 'METER', projectField: 'lengthM' },
  { key: 'breite', section: 'Beckendimensionierung', label: 'Breite', mode: 'NOTE',
    isNumeric: true, unit: 'METER', projectField: 'widthM' },
  { key: 'hoehe', section: 'Beckendimensionierung', label: 'Höhe', mode: 'NOTE',
    isNumeric: true, unit: 'METER', projectField: 'heightM' },
  { key: 'wasserspiegel', section: 'Beckendimensionierung', label: 'Höhe Wasserspiegel', mode: 'NOTE',
    isNumeric: true, unit: 'METER', projectField: 'waterLevelM' },

  // ── 1. Hauptwelle ──────────────────────────────────────────────────────────
  { key: 'hauptwelle', section: '1. Hauptwelle', label: 'Hauptwelle', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 3200,
    options: ['Rohr ø88,9 x 4', 'Rohr ø114,3 x 3', 'Rohr ø 156 x 3', 'Rohr ø 206 x 3', 'Rohr 256 x 3', 'Rohr ø306 x 3', 'GFK ø 306'],
    hint: 'kein Standardartikel — meist Platzhalter (Zuschnitt)' },
  { key: 'antriebskettenrad', section: '1. Hauptwelle', label: 'Antriebskettenrad', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 1528,
    options: ['RLL717-3 (Z=28)', 'RLN647 (Z=23)', 'RLP359 (Z=45)', 'RLN656 (Z=23)', 'RL140334 (Z=29)', 'RL170015 (Z=28)'],
    hint: 'üblich: RLL717-3 (Z=28)' },
  { key: 'foerderkettenrad', section: '1. Hauptwelle', label: 'Förderkettenrad', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 1123,
    options: ['RL200241 (Z=23)', 'RL120130 (Z=23)', 'RLN647 (Z=23)', 'RLO038 (Z=19)', 'RLN556 (Z=19)'],
    hint: 'üblich: RLN647 (Z=23)' },
  { key: 'wellenstummel', section: '1. Hauptwelle', label: 'Wellenstummel', mode: 'NOTE',
    options: ['Rohr', 'Vollwelle'] },

  // ── 2. Antriebseinheit ─────────────────────────────────────────────────────
  { key: 'antriebseinheit', section: '2. Antriebseinheit', label: 'Antriebseinheit', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 5100,
    options: ['R57R37 DRN71', 'R67R37 DRN71', 'R87R57 DRN71', 'R97R57 DRN71', 'K57R37 DRN71', 'K67R37 DRN71', 'K87R57 DRN71'],
    hint: 'üblich: R57R37 DRN71' },
  { key: 'leistung', section: '2. Antriebseinheit', label: 'Leistung', mode: 'NOTE',
    hint: 'ergibt sich aus dem Antrieb' },
  { key: 'antriebskette', section: '2. Antriebseinheit', label: 'Antriebskette', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'METER', commodityGroupCode: 1500,
    options: ['HB78 geölt', 'HB78 schwarz', 'HB78 ohne', 'NH78', 'NCS720S', 'NCS360'],
    hint: 'üblich: NH78' },
  { key: 'antriebsritzel', section: '2. Antriebseinheit', label: 'Antriebsritzel', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 1511,
    options: ['RL140316 (Z=11)', 'RL150156 (Z=15)', 'RL180244 (Z=11)', 'RLJ691-3 (Z=11)'],
    hint: 'üblich: RLJ691-3 (Z=11)' },
  { key: 'stromwandler', section: '2. Antriebseinheit', label: 'Stromwandler', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 5005, options: ['WSW60'], hint: 'üblich: WSW60' },
  { key: 'wirkleistungsmesser', section: '2. Antriebseinheit', label: 'Wirkleistungsmesser', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 5002,
    options: ['G2BM400V12L10', 'G4BM480V12AFTL20', 'G4BM690V16AT20'], hint: 'üblich: G4BM480V12AFTL20' },
  { key: 'trafo', section: '2. Antriebseinheit', label: 'Trafo zu Wirkleistungsmesser', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 5002,
    options: ['TR3-230VAC', 'TR3-400VAC'], hint: 'üblich: TR3-230VAC' },

  // ── 3. Balken ──────────────────────────────────────────────────────────────
  // Balken sind speziell (Anweisung Tech-Lead 17.08.): entweder ein ablängbarer
  // Balken aus dem Artikelstamm oder der Mutterartikel mit Stück + mm je Stück
  // (Ablängung, ADR 0040). Deshalb Einheit STÜCK und nicht Meter — die Meter
  // rechnet die Stücklisten-Vorlage daraus (Stück × Länge). Die Stückzahl dieser
  // Zeile IST die Balkenanzahl, die die Kettenberechnung braucht; das frühere
  // Angabe-Feld „Balkenanzahl" daneben ist damit entfallen (ADR 0094).
  { key: 'balken', section: '3. Balken', label: 'Balken', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 2010,
    options: ['Pro9', 'Hochlast', 'UltraSL 5,5', 'UltraSL 5,5-280'],
    hint: 'üblich: Pro9 — Stückzahl und Länge je Stück eintragen' },
  { key: 'abdeckkappen', section: '3. Balken', label: 'Abdeckkappen vorab einkleben', mode: 'NOTE',
    options: ['Ja', 'Nein'] },
  { key: 'balken-material', section: '3. Balken', label: 'Material', mode: 'NOTE',
    options: ['Polyester', 'Vinylester'] },
  { key: 'bodengleitschuh', section: '3. Balken', label: 'Bodengleitschuh', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 2100,
    options: ['PE (weiß)', 'PU (orange)'], hint: 'üblich: PE (weiß)' },
  { key: 'ruecklaufschuh', section: '3. Balken', label: 'Rücklaufschuh', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 2110,
    options: ['PE (schwarz)', 'PU', 'gekantet PE'], hint: 'üblich: PE (schwarz)' },
  { key: 'buersten', section: '3. Balken', label: 'Bürsten', mode: 'NOTE',
    options: ['keine', '20mm', '30mm', '40mm'], commodityGroupCode: 3700,
    hint: 'mit Bürsten: auf Artikel umstellen (WG 3700)' },

  // ── 4. Förderkette ─────────────────────────────────────────────────────────
  { key: 'foerderkette', section: '4. Förderkette', label: 'Förderkette', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'METER', commodityGroupCode: 1100,
    options: ['NCS720S', 'LoopChain', 'API 720S'], hint: 'üblich: NCS720S' },

  // ── 5. Umlenkstummel ───────────────────────────────────────────────────────
  { key: 'umlenkstummel', section: '5. Umlenkstummel', label: 'Umlenkstummel', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 3300, options: ['US80', 'US88,9'], hint: 'üblich: US88,9' },
  { key: 'umlenkrad', section: '5. Umlenkstummel', label: 'Umlenkrad', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 1119,
    options: ['RLN767 (Z=17)', 'RLN134-3 (Z=19)', 'RLJ673-3 (Z=19)', 'RLP276 (Z=19)', 'RL120142 (Z=23)', 'RLJ734-3 (Z=23)', 'RLO248 (Z=23)'],
    hint: 'üblich: RLN134-3 (Z=19)' },
  { key: 'spannvorrichtung', section: '5. Umlenkstummel', label: 'Spannvorrichtung', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 3310,
    options: ['USV_2W', 'USV_4W', 'ULW_SV', 'USV_KS_Neu', 'Keine'], hint: 'ohne Spannvorrichtung: auf Angabe stellen' },

  // ── 6. Rücklaufschiene ─────────────────────────────────────────────────────
  { key: 'ruecklaufschiene', section: '6. Rücklaufschiene', label: 'Rücklaufschiene', mode: 'NOTE',
    options: ['Edelstahl', 'Kunststoff'], commodityGroupCode: 3400,
    hint: 'als Position: Artikel aus WG 3400' },

  // ── 7. Bodengleitstreifen ──────────────────────────────────────────────────
  { key: 'bodengleitstreifen', section: '7. Bodengleitstreifen', label: 'Bodengleitstreifen', mode: 'NOTE',
    options: ['Standard', 'API', '15mm'], commodityGroupCode: 2200,
    hint: 'als Position: Gleitstreifen aus WG 2200' },
  { key: 'washer', section: '7. Bodengleitstreifen', label: 'Washer', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 2200,
    options: ['Edelstahl', 'Kunststoff'], hint: 'üblich: Kunststoff' },

  // ── 8. Gleichlaufüberwachung ───────────────────────────────────────────────
  { key: 'gleichlaufueberwachung', section: '8. Gleichlaufüberwachung', label: 'Gleichlaufüberwachung', mode: 'NOTE',
    options: ['Standard', 'API', 'Sonder'] },
  { key: 'sensor', section: '8. Gleichlaufüberwachung', label: 'Sensor', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 5003,
    options: ['NBN8-18GK60-E2-10M', 'NBB5-18GK50-E2-15M', 'NBN8-18GK50-A2-15M', 'NCB5-18GM40-N0-15M', 'NJ5-18GK-N-10M'],
    hint: 'üblich: NBN8-18GK60-E2-10M' },

  // ── 9. Skimrinne ───────────────────────────────────────────────────────────
  { key: 'skimrinne', section: '9. Skimrinne', label: 'Skimrinne', mode: 'ARTICLE',
    defaultQuantity: 1, unit: 'PIECE', commodityGroupCode: 3600,
    options: ['Edelstahl', 'GFK'], hint: 'kein Standardartikel — meist Platzhalter' },
  { key: 'verstellkonsole', section: '9. Skimrinne', label: 'Verstellkonsole', mode: 'NOTE',
    options: ['Kettenverstellung', 'Spindelverstellung'], commodityGroupCode: 3500,
    hint: 'als Position: Konsole aus WG 3500' },
  { key: 'wandlager', section: '9. Skimrinne', label: 'Wandlager', mode: 'NOTE',
    options: ['Gleitlagerung', 'Rollenlagerung'], commodityGroupCode: 3500,
    hint: 'als Position: Wandlager aus WG 3500' },

  // ── Sonstiges ──────────────────────────────────────────────────────────────
  { key: 'sonstiges', section: 'Sonstiges', label: 'Sonstiges', mode: 'NOTE' },
];

/**
 * Reihenfolge der Zeilen im Blatt: Abschnitte in Vorlagen-Reihenfolge, selbst
 * angelegte Abschnitte danach alphabetisch, innerhalb des Abschnitts nach
 * `position`. Liegt hier, weil API und UI dieselbe Reihenfolge zeigen müssen —
 * zwei Implementierungen wären zwei Wahrheiten.
 */
export function compareLayoutRows(
  a: { section: string; position: number },
  b: { section: string; position: number },
): number {
  const rankA = layoutSectionRank(a.section);
  const rankB = layoutSectionRank(b.section);
  if (rankA !== rankB) return rankA - rankB;
  // Gleicher Rang und beide unbekannt → nach Abschnittsname, damit die Zeilen
  // eines selbst angelegten Abschnitts beieinander bleiben.
  if (a.section !== b.section) return a.section.localeCompare(b.section, 'de');
  return a.position - b.position;
}

/** Rang eines Abschnitts in der Vorlage; selbst angelegte Abschnitte hinten. */
export function layoutSectionRank(section: string): number {
  const index = LAYOUT_SECTIONS.indexOf(section as LayoutSection);
  return index === -1 ? LAYOUT_SECTIONS.length : index;
}

/** Vorlagenzeile zu einem Schlüssel (Anzeige-Zusatz wie Optionen/Hinweis). */
export function layoutTemplateRow(key: string | null | undefined): LayoutTemplateRow | undefined {
  if (!key) return undefined;
  return LAYOUT_TEMPLATE_ROWS.find((row) => row.key === key);
}
